// src/pages/pengamatan/sensus-populasi-anakan-pisang/draftActions.js
import { useState } from 'react';

/**
 * Custom hook untuk menangani aksi-aksi terkait perubahan status (Draft, Processed, Approved)
 */
export const useDraftActions = ({
  selectedItems,
  orders,
  updateStatusPengamatan,
  onStatusUpdate,
  onReload,
  onMenuClose
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState('');
  const [loading, setLoading] = useState(false);

  /**
   * Mengecek apakah semua item yang dipilih memiliki status tertentu
   */
  const allSelectedItemsHaveStatus = (status) => {
    if (selectedItems.length === 0) return false;
    
    return selectedItems.every(itemId => {
      const order = orders.find(order => order.id === itemId);
      return order && order.status_pengamatan === status;
    });
  };

  /**
   * Mengecek apakah ada minimal 1 item yang memiliki status tertentu
   */
  const hasAnyItemWithStatus = (status) => {
    if (selectedItems.length === 0) return false;
    
    return selectedItems.some(itemId => {
      const order = orders.find(order => order.id === itemId);
      return order && order.status_pengamatan === status;
    });
  };

  /**
   * Mengecek apakah semua item yang dipilih sudah memiliki status Draft
   */
  const allSelectedItemsAreDraft = () => {
    return allSelectedItemsHaveStatus("Draft");
  };

  /**
   * Mengecek apakah semua item yang dipilih sudah memiliki status Processed
   */
  const allSelectedItemsAreProcessed = () => {
    return allSelectedItemsHaveStatus("Processed");
  };

  /**
   * Mengecek apakah semua item yang dipilih sudah memiliki status Approved
   */
  const allSelectedItemsAreApproved = () => {
    return allSelectedItemsHaveStatus("Approved");
  };

  /**
   * Mengecek apakah selected items memiliki status campuran (Approved dan Processed)
   */
  const hasMixedApprovedAndProcessed = () => {
    if (selectedItems.length < 2) return false;
    
    const hasApproved = hasAnyItemWithStatus("Approved");
    const hasProcessed = hasAnyItemWithStatus("Processed");
    
    return hasApproved && hasProcessed;
  };

  /**
   * Mengecek apakah semua item yang dipilih memiliki status Processed
   */
  const allSelectedItemsAreOnlyProcessed = () => {
    if (selectedItems.length === 0) return false;
    
    return selectedItems.every(itemId => {
      const order = orders.find(order => order.id === itemId);
      return order && order.status_pengamatan === "Processed";
    });
  };

  /**
   * Logika untuk menampilkan menu "Ubah Ke Draft"
   */
  const showDraftMenu = () => {
    // Selalu tampilkan kecuali jika semua item sudah Draft
    return !allSelectedItemsAreDraft();
  };

  /**
   * Logika untuk menampilkan menu "Ubah Ke Processed"
   */
  const showProcessedMenu = () => {
    // Jangan tampilkan jika ada campuran status Approved dan Processed
    if (hasMixedApprovedAndProcessed()) {
      return false;
    }
    
    // Jangan tampilkan jika semua item sudah memiliki status Processed
    if (allSelectedItemsAreProcessed()) {
      return false;
    }
    
    // Tampilkan untuk kasus lainnya (semua Approved, atau kombinasi lainnya)
    return true;
  };

  /**
   * Logika untuk menampilkan menu "Ubah Ke Approved"
   */
  const showApprovedMenu = () => {
    // Jangan tampilkan jika ada campuran status Approved dan Processed
    if (hasMixedApprovedAndProcessed()) {
      return false;
    }
    
    // Jangan tampilkan jika semua item sudah memiliki status Approved
    if (allSelectedItemsAreApproved()) {
      return false;
    }
    
    // Tampilkan untuk kasus lainnya (semua Processed, atau kombinasi lainnya)
    return true;
  };

  /**
   * Membuka dialog konfirmasi untuk perubahan status
   */
  const handleOpenDialog = (action) => {
    setDialogAction(action);
    setConfirmDialogOpen(true);
    onMenuClose();
  };

  /**
   * Menutup dialog konfirmasi
   */
  const handleCloseDialog = () => {
    setConfirmDialogOpen(false);
  };

  /**
   * Menangani konfirmasi perubahan status
   */
  const handleConfirmAction = async () => {
    setLoading(true);
    
    try {
      const success = await updateStatusPengamatan(
        selectedItems, 
        dialogAction, 
        () => {
          // Callback yang akan dipanggil setelah berhasil
          if (onReload) {
            onReload();
          }
        }
      );
      
      if (success) {
        onStatusUpdate({
          open: true,
          message: `Status pengamatan berhasil diubah ke ${dialogAction}. Halaman akan dimuat ulang...`,
          severity: 'success'
        });
      } else {
        onStatusUpdate({
          open: true,
          message: 'Gagal mengubah status pengamatan',
          severity: 'error'
        });
      }
    } catch (error) {
      onStatusUpdate({
        open: true,
        message: 'Terjadi kesalahan saat mengubah status',
        severity: 'error'
      });
      setLoading(false);
      setConfirmDialogOpen(false);
    }
  };

  return {
    confirmDialogOpen,
    dialogAction,
    loading,
    // Status checking functions
    allSelectedItemsAreDraft,
    allSelectedItemsAreProcessed,
    allSelectedItemsAreApproved,
    allSelectedItemsAreOnlyProcessed,
    hasMixedApprovedAndProcessed,
    // Menu display logic
    showDraftMenu,
    showProcessedMenu,
    showApprovedMenu,
    // Action handlers
    handleOpenDialog,
    handleCloseDialog,
    handleConfirmAction
  };
};

/**
 * Utility functions untuk mengecek status items (standalone version)
 */
export const statusUtils = {
  /**
   * Mengecek apakah semua item yang dipilih memiliki status tertentu
   */
  allSelectedItemsHaveStatus: (selectedItems, orders, status) => {
    if (selectedItems.length === 0) return false;
    
    return selectedItems.every(itemId => {
      const order = orders.find(order => order.id === itemId);
      return order && order.status_pengamatan === status;
    });
  },

  /**
   * Mengecek apakah ada minimal 1 item yang memiliki status tertentu
   */
  hasAnyItemWithStatus: (selectedItems, orders, status) => {
    if (selectedItems.length === 0) return false;
    
    return selectedItems.some(itemId => {
      const order = orders.find(order => order.id === itemId);
      return order && order.status_pengamatan === status;
    });
  },

  /**
   * Mengecek apakah selected items memiliki status campuran
   */
  hasMixedStatus: (selectedItems, orders, status1, status2) => {
    if (selectedItems.length < 2) return false;
    
    const hasStatus1 = statusUtils.hasAnyItemWithStatus(selectedItems, orders, status1);
    const hasStatus2 = statusUtils.hasAnyItemWithStatus(selectedItems, orders, status2);
    
    return hasStatus1 && hasStatus2;
  }
};

export default useDraftActions;